import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { CustomInput, CustomTextarea, CustomToggle } from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';
import Table from '../Components/Tables';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import ItemsPickerModal from '../Components/ItemsPickerModal';
import { getItemId, PickerColumn } from '../Components/ItemsPickerModalTypes';

import {
  InformationPackageApiProvider,
  useInformationPackageApi,
} from '../Services/InformationPackageApiProvider';
import {
  IPCollectionsApiProvider,
  useIPCollectionsApi,
} from '../Services/IPCollectionsApiProvider';
import { PackageModel } from '../Services/IPCollectionsApiProviderTypes';
import { formatDateUTC } from '../utils/formatDate';
import { isEmpty } from '../utils/validationUtil';
import './InformationPackageCollection.css';

interface InformationPackage {
  key: string;
  name: string;
  size: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const ipCollectionColumns = [
  {
    name: 'ID',
    selector: (row: PackageModel) => row.id,
    sortable: false,
  },
  {
    name: 'IP Name',
    selector: (row: PackageModel) => row.name,
    sortable: true,
  },
];

const informationPackageColumns: PickerColumn<InformationPackage>[] = [
  {
    key: 'name',
    header: 'Name',
    widthClass: 'items-picker-col--flex-3',
    render: (item) => (
      <>
        <div className="items-picker-name">{item.name}</div>
        <div className="items-picker-id small-font">id: {getItemId(item)}</div>
      </>
    ),
    searchable: true,
    getSearchValue: (item) => item.name || '',
  },
  {
    key: 'size',
    header: 'Size',
    widthClass: 'items-picker-col--flex-1',
    render: (item) => <div className="items-picker-description small-font">{item.size || '-'}</div>,
  },
  {
    key: 'modified',
    header: 'Modified',
    widthClass: 'items-picker-col--flex-2',
    render: (item) => (
      <div className="items-picker-description small-font">
        {formatDateUTC(item.updated_at) || '-'}
      </div>
    ),
  },
];

function IPCollectionContent({ canEdit }: { canEdit: boolean }) {
  const navigate = useNavigate();
  const { organizationId, ipCollectionId } = useParams();
  const { showAlert } = useAlert();

  const { getIPCollectionById, createIPCollection, updateIPCollection } = useIPCollectionsApi();

  const { getInformationPackages } = useInformationPackageApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [isNew, setIsNew] = useState<boolean>(true);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isPickerOpen, setIsPickerOpen] = useState<boolean>(false);

  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    packages: PackageModel[];
    is_active: boolean;
  }>({
    name: '',
    description: '',
    packages: [],
    is_active: true,
  });

  useEffect(() => {
    const isNewIPCollection = ipCollectionId === 'new-ip-collection';

    setIsNew(isNewIPCollection);
    setIsDisabled(!canEdit);

    if (!isNewIPCollection) {
      fetchData();
    }
  }, [ipCollectionId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getIPCollectionById(ipCollectionId);
      setFormData({
        name: response.name,
        description: response.description,
        packages: response.packages,
        is_active: response.is_active,
      });
    } catch (error) {
      showAlert('fail', 'Failed to fetch information package collection');
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (): Promise<void> => {
    if (isEmpty(formData.name)) {
      showAlert('fail', 'Please enter IP collection name');
      return;
    }

    if (isEmpty(formData.description)) {
      showAlert('fail', 'Please enter IP collection description');
      return;
    }

    const data = {
      name: formData.name,
      description: formData.description,
      is_active: formData.is_active,
      packages: formData.packages.map((p) => p.id),
    };

    setLoading(true);

    try {
      if (isNew) {
        await createIPCollection(data);
        showAlert('success', 'Information package collection was created successfully');
      } else {
        await updateIPCollection(ipCollectionId, data);
        showAlert('success', 'Information package collection was updated successfully');
      }
      navigate(`/organizations/${organizationId}/information-package-collections`, {
        state: { organizationId },
      });
    } catch (error) {
      showAlert('fail', 'Failed to save information package collection');
    } finally {
      setLoading(false);
    }
  };

  function onDelete(selectedIds: (string | number)[]): void {
    if (!selectedIds || selectedIds.length === 0) return;
    const toRemove = new Set(selectedIds.map(String));
    setFormData((prev) => ({
      ...prev,
      packages: prev.packages.filter((pkg) => !toRemove.has(String(pkg.id))),
    }));
  }

  const onCancel = (): void => {
    navigate(`/organizations/${organizationId}/information-package-collections`, {
      state: { organizationId },
    });
  };

  const handleNameChange = useCallback(
    (_id: string, value: string) => setFormData((prev) => ({ ...prev, name: value })),
    [],
  );

  const handleDescriptionChange = useCallback(
    (value: string) => setFormData((prev) => ({ ...prev, description: value })),
    [],
  );

  const handleStatusChange = useCallback(
    (_id: string, value: boolean) => setFormData((prev) => ({ ...prev, is_active: value })),
    [],
  );

  const fetchIPs = useCallback(
    async ({
      limit,
      pageToken,
      searchQuery,
    }: {
      limit: number;
      pageToken: string | null;
      searchQuery: string | null;
    }) => {
      try {
        const skip = pageToken ? Number(pageToken) : 0;
        const response = await getInformationPackages(skip, limit, searchQuery || undefined);
        const items = response.items.map((item) => ({
          id: item.key,
          key: item.key,
          name: item.name,
          size: '-',
          is_active: item.is_active,
          created_at: item.created_at,
          updated_at: item.updated_at,
        }));

        const total = Number(response.totalCount ?? 0);
        const nextSkip = skip + items.length;
        const hasMore = total > 0 ? nextSkip < total : items.length === limit;

        return {
          items,
          nextPageToken: hasMore ? String(nextSkip) : null,
          hasMore,
        };
      } catch (error) {
        showAlert('fail', 'Failed to fetch information packages');
        return { items: [], nextPageToken: null, hasMore: false };
      }
    },
    [],
  );

  const handleOpenPicker = useCallback(() => setIsPickerOpen(true), []);
  const handleClosePicker = useCallback(() => setIsPickerOpen(false), []);

  const handleAddPackages = useCallback((picked: InformationPackage[]) => {
    setFormData((prev) => {
      const existingIds = new Set(prev.packages.map((p) => String(p.id)));
      const additions = picked
        .filter((p) => !existingIds.has(getItemId(p)))
        .map((p) => ({
          id: getItemId(p),
          name: p.name,
          created_at: p.created_at,
          updated_at: p.updated_at,
          is_active: p.is_active,
        }));
      return { ...prev, packages: [...prev.packages, ...additions] };
    });
    setIsPickerOpen(false);
  }, []);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <h2 className="h2-title">{formData.name}</h2>
          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Main Settings</h4>

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">IP Collection Name</div>
                  <div className="small-font">Enter IP collection name</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="chat-name"
                    label="IP Collection Name"
                    placeholder="Enter IP collection name"
                    type="text"
                    icon={<i className="fa-solid fa-comments"></i>}
                    value={formData.name}
                    onChange={handleNameChange}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">IP Collection Description</div>
                  <div className="small-font">Enter IP collection description</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomTextarea
                    id="ip-collection-description"
                    label="IP Collection Description"
                    name="ip-collection-description"
                    addedText={formData.description}
                    onChange={handleDescriptionChange}
                    resizable={false}
                    disabled={isDisabled}
                    height="200px"
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Activate IP Collection</div>
                  <div className="small-font">Set IP Collection to be active or inactive</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomToggle
                    id="toggle-status"
                    label={formData.is_active ? 'Active' : 'Inactive'}
                    checked={formData.is_active}
                    onChange={handleStatusChange}
                    disabled={isDisabled}
                  />
                </div>
              </div>
            </div>

            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <div className="input-block d-flex flex-column justify-between">
                <Table
                  title="Information Packages"
                  columns={ipCollectionColumns}
                  data={formData.packages}
                  selectableRows
                  selectableRowsHighlight
                  singleSelect={false}
                  pagination={false}
                  onDelete={onDelete}
                />
                <div className="ip-collection-add-row d-flex justify-end">
                  <Button
                    variant="primary"
                    size="medium"
                    disabled={isDisabled}
                    onClick={handleOpenPicker}
                  >
                    Add IP
                  </Button>
                </div>
              </div>
            </div>

            <div className="buttons-container d-flex justify-end">
              <Button variant="primary" size="medium" disabled={isDisabled} onClick={onSave}>
                Save
              </Button>
              <Button variant="danger" size="medium" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ItemsPickerModal
        isOpen={isPickerOpen}
        onClose={handleClosePicker}
        title="Select Information Packages"
        disabled={isDisabled}
        selectedIds={formData.packages.map((p) => p.id)}
        onSaveSelected={handleAddPackages}
        columns={informationPackageColumns}
        fetchItems={fetchIPs}
      />
    </div>
  );
}

function InformationPackageCollection({ canEdit }: { canEdit: boolean }) {
  return (
    <ConfirmationProvider>
      <InformationPackageApiProvider>
        <IPCollectionsApiProvider>
          <IPCollectionContent canEdit={canEdit} />
        </IPCollectionsApiProvider>
      </InformationPackageApiProvider>
    </ConfirmationProvider>
  );
}

export default InformationPackageCollection;
